import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type Client, listClients } from "../api/crm.ts";

const STATUS_COLORS: Record<string, string> = {
  CREATED: "text-blue-400 bg-blue-900/30",
  ONBOARDED: "text-yellow-400 bg-yellow-900/30",
  ACTIVE: "text-green-400 bg-green-900/30",
  PAUSED: "text-orange-400 bg-orange-900/30",
  COMPLETED: "text-gray-300 bg-gray-700",
  ARCHIVED: "text-gray-500 bg-gray-800",
};

export function ClientsPage() {
  const { token } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    listClients(token, { search: search || undefined, status: status || undefined, limit, offset: page * limit })
      .then((res) => { setClients(res.items); setTotal(res.total); })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load clients"))
      .finally(() => setLoading(false));
  }, [token, search, status, page]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Clients</h1>
          <span className="text-sm text-gray-500">{total} total</span>
        </div>

        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search clients…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(0); }}
            className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
          >
            <option value="">All statuses</option>
            {["CREATED", "ONBOARDED", "ACTIVE", "PAUSED", "COMPLETED"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}
        {!loading && clients.length === 0 && <p className="text-gray-500">No clients found.</p>}

        <div className="space-y-2">
          {clients.map((c) => (
            <div key={c.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-white">{c.name}</p>
                  <div className="flex gap-3 mt-1 text-xs text-gray-500">
                    {c.contractValue != null && <span>${c.contractValue.toLocaleString()} {c.currency}</span>}
                    {c.contractStart && <span>from {new Date(c.contractStart).toLocaleDateString()}</span>}
                    {c.contractEnd && <span>to {new Date(c.contractEnd).toLocaleDateString()}</span>}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-medium shrink-0 ${STATUS_COLORS[c.status] ?? "text-gray-400 bg-gray-800"}`}>
                  {c.status}
                </span>
              </div>
            </div>
          ))}
        </div>

        {total > limit && (
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Previous</button>
            <span className="text-sm text-gray-500 self-center">{page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= total} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
